import React, { useEffect, useState } from "react";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Title from "./TabTitle";
import { getMatterAllInfo } from "api";
import { useMatch, useLocation, useNavigate } from "react-router-dom";
import LoadingScreen from "components/common/Loading";
import MyMattersPageComponent from "components/Matters/MattersGridComponent";
import CaseFileView from "pages/CaseFile";
import Clients from "pages/Clients/ClientsList";
import { useAllowedLiscence } from "hooks/useAllowedLiscence";
import { Tabs } from "components/Enums/TabEnums.ts";
import Contacts from "pages/Contacts/Contact";
import Onboarding from "pages/Onboarding/Onboarding";
import useWindowEventHandlers from "hooks/useEventDispatcher";
import { useCompany } from "services/companyService";
import WorkDone from "pages/FeeEarner/WorkDone";
import Tasks from "pages/Tasks/Tasks";

const getMatterTabName = (item) => {

    if (item?.caseHeaderText) {
        return item.caseHeaderText;
    }
   
    return item.matterRef;
};

const FIXED_TABS = [
    { id: "0", name: "Clients List" },
    { id: "1", name: "Contacts List" },
    { id: "2", name: "Matter List" },
    { id: "3", name: "Client Onboarding" },
    { id: "4", name: "Work Done" },
    { id: "5", name: "Task List" },
];

const reorderTabs = (tabs) => {
    const orderedTabs = [
        tabs.find((tab) => tab.id === FIXED_TABS[0].id), // Clients List
        tabs.find((tab) => tab.id === FIXED_TABS[1].id), // Contacts List
        tabs.find((tab) => tab.id === FIXED_TABS[2].id), // Matters List
        tabs.find((tab) => tab.id === FIXED_TABS[3].id), // Client Onboarding
        tabs.find((tab) => tab.id === FIXED_TABS[4].id), // Work Done
        tabs.find((tab) => tab.id === FIXED_TABS[5].id), // Task List
        ...tabs.filter(
            (tab) => !FIXED_TABS.some((fixedTab) => fixedTab.id === tab.id)
        ), // Other tabs
    ];
    return orderedTabs.filter(Boolean); // Remove undefined entries
};

const MatterTabs = React.memo(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const { registerHandler } = useWindowEventHandlers();
    const { allowed } = useAllowedLiscence(Tabs.MATTERS);
    const [selected, setSelected] = useState(0);
    const [tabs, setTabs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const { data: company } = useCompany();
    const isClientOnboardingEnabled = company?.modules?.ClientOnboarding;

    const params = useMatch("/matters/:id");
    const onboardingParams = useMatch("/clients/onboarding");

    const clientParams = useMatch("/clients");
    const contactParams = useMatch("/contacts");
    const workDoneParams = useMatch("/workdone");
    const tasksParams = useMatch("/tasks");

    useEffect(() => {
        if (params?.params?.id) {
            const mattersDataGetter = async () => {
                try {
                    const data = await getMatterAllInfo(params.params.id);
                    const matterTabName = getMatterTabName(data.data.data);
                    const existingTab = tabs.find((tab) => tab.name === matterTabName);

                    if (existingTab) {
                        setSelected(tabs.indexOf(existingTab));
                    } else {
                        setTabs((prevTabs) =>
                            reorderTabs([
                                ...prevTabs,
                                {
                                    name: matterTabName,
                                    id: `matter-${params.params.id}`,
                                    matterId: params.params.id,
                                },
                            ])
                        );
                        setSelected(tabs.length);
                    }
                } catch (error) {
                    alert(
                        "No matter exists against this Id. Try again with different Id"
                    );
                    navigate("/matters");
                }
            };
            mattersDataGetter();
        }
    }, [params, navigate]);

    useEffect(() => {
        if (clientParams) {
            const existingTab = tabs.find((tab) => tab.id === FIXED_TABS[0].id);
            if (existingTab) {
                setSelected(tabs.indexOf(existingTab));
            } else {
                let existingNewTab = 0;
                setTabs((prevTabs) => {
                    const tabsReturendData = reorderTabs([FIXED_TABS[0], ...prevTabs]);
                    existingNewTab = tabsReturendData.indexOf(
                        tabsReturendData.find((tab) => tab.id === FIXED_TABS[0].id)
                    );
                    return tabsReturendData;
                });
                setTimeout(() => {
                    setSelected(existingNewTab);
                }, 50);
            }
        }
    }, [clientParams]);

    useEffect(() => {
        if (contactParams) {
            const existingTab = tabs.find((tab) => tab.id === FIXED_TABS[1].id);
            if (existingTab) {
                setSelected(tabs.indexOf(existingTab));
            } else {
                let existingNewTab = 0;
                setTabs((prevTabs) => {
                    const tabsReturendData = reorderTabs([FIXED_TABS[1], ...prevTabs]);
                    existingNewTab = tabsReturendData.indexOf(
                        tabsReturendData.find((tab) => tab.id === FIXED_TABS[1].id)
                    );
                    return tabsReturendData;
                });
                setTimeout(() => {
                    setSelected(existingNewTab);
                }, 50);
            }
        }
    }, [contactParams]);

    useEffect(() => {
        if (location.pathname === "/matters") {
            const existingTab = tabs.find((tab) => tab.id === FIXED_TABS[2].id);
            if (existingTab) {
                setSelected(tabs.indexOf(existingTab));
            } else {
                let existingNewTab = 0;
                setTabs((prevTabs) => {
                    const tabsReturendData = reorderTabs([FIXED_TABS[2], ...prevTabs]);
                    existingNewTab = tabsReturendData.indexOf(
                        tabsReturendData.find((tab) => tab.id === FIXED_TABS[2].id)
                    );
                    return tabsReturendData;
                });
                setTimeout(() => {
                    setSelected(existingNewTab);
                }, 50);
            }
        }
    }, [location.pathname]);

    useEffect(() => {
        if (onboardingParams) {
            if (!isClientOnboardingEnabled) {
                navigate("/");
                return;
            }
            const existingTab = tabs.find((tab) => tab.id === FIXED_TABS[3].id);
            if (existingTab) {
                setSelected(tabs.indexOf(existingTab));
            } else {
                let existingNewTab = 0;
                setTabs((prevTabs) => {
                    const tabsReturendData = reorderTabs([FIXED_TABS[3], ...prevTabs]);
                    existingNewTab = tabsReturendData.indexOf(
                        tabsReturendData.find((tab) => tab.id === FIXED_TABS[3].id)
                    );
                    return tabsReturendData;
                });
                setTimeout(() => {
                    setSelected(existingNewTab);
                }, 50);
            }
        }
    }, [onboardingParams, isClientOnboardingEnabled, navigate]);

    useEffect(() => {
        if (workDoneParams) {
            const existingTab = tabs.find((tab) => tab.id === FIXED_TABS[4].id);
            if (existingTab) {
                setSelected(tabs.indexOf(existingTab));
            } else {
                let existingNewTab = 0;
                setTabs((prevTabs) => {
                    const tabsReturendData = reorderTabs([FIXED_TABS[4], ...prevTabs]);
                    existingNewTab = tabsReturendData.indexOf(
                        tabsReturendData.find((tab) => tab.id === FIXED_TABS[4].id)
                    );
                    return tabsReturendData;
                });
                setTimeout(() => {
                    setSelected(existingNewTab);
                }, 50);
            }
        }
    }, [workDoneParams]);

    useEffect(() => {
        if (tasksParams) {
            const existingTab = tabs.find((tab) => tab.id === FIXED_TABS[5].id);
            if (existingTab) {
                setSelected(tabs.indexOf(existingTab));
            } else {
                let existingNewTab = 0;
                setTabs((prevTabs) => {
                    const tabsReturendData = reorderTabs([FIXED_TABS[5], ...prevTabs]);
                    existingNewTab = tabsReturendData.indexOf(
                        tabsReturendData.find((tab) => tab.id === FIXED_TABS[5].id)
                    );
                    return tabsReturendData;
                });
                setTimeout(() => {
                    setSelected(existingNewTab);
                }, 50);
            }
        }
    }, [tasksParams]);


    useEffect(() => {
        registerHandler("CloseCasefile", (matterId) => {
            try {
                removeTab(
                    tabs.find((item) => item.matterId == matterId)?.name,
                    matterId
                );
                return "Case File Closed";
            } catch (error) { }
        });
    }, []);

    const navigateToTab = (tab) => {
        if (tab.matterId) navigate(`/matters/${tab.matterId}`);
        else if (tab.name === "Clients List") navigate("/clients");
        else if (tab.name === "Client Onboarding") navigate("/clients/onboarding");
        else if (tab.name === "Contacts List") navigate("/contacts");
        else if (tab.name === "Work Done") navigate("/workdone");
        else if (tab.name === "Task List") navigate("/tasks");
        else navigate("/matters");
    };

    const handleSelect = (e) => {
        setSelected(e.selected);
        navigateToTab(tabs[e.selected]);
    };

    const removeTab = (tabName, matterId) => {
        const closedIndex = tabs.findIndex((t) => t.name === tabName);
        const newTabs = reorderTabs(tabs.filter((t) => t.name !== tabName));
        setTabs(newTabs);

        if (!newTabs.length) {
            navigate("/");
            setSelected(0);
        } else {
            const newSelected =
                closedIndex === selected
                    ? Math.min(selected, newTabs.length - 1)
                    : closedIndex < selected
                        ? selected - 1
                        : selected;
            setSelected(newSelected);
            navigateToTab(newTabs[newSelected]);
        }

        if (
            ![
                "Clients List",
                "Contacts List",
                "Matter List",
                "Client Onboarding",
                "Work Done",
                "Task List",
            ].includes(tabName)
        ) {
            try {
                window.chrome.webview.postMessage("CasefileClosed:" + matterId);
            } catch (error) { }
        }
    };
    return allowed ? (
        <TabStrip
            selected={selected}
            onSelect={handleSelect}
            keepTabsMounted={true}
            className="navigationTabs h-100"
        >
            {tabs?.map((item, index) => (
                <TabStripTab
                    title={
                        <Title
                            content={item.name}
                            onTabRemove={() => removeTab(item.name, item?.matterId)}
                            selected={tabs[selected]?.name}
                            count={tabs.length}
                            maxWidth={"220px"}
                        />
                    }
                    key={index}
                    contentClassName="h-100"
                >
                    <div className="h-100 w-100">
                        {item.id === "0" && <Clients />}
                        {item.id === "1" && <Contacts />}
                        {item.id === "2" && (
                            <MyMattersPageComponent component={"matters"} />
                        )}
                        {item.matterId && (
                            <CaseFileView
                                selTab={selected}
                                reference={item.name}
                                id={item.matterId}
                                selectedid={selected}
                            />
                        )}
                        {item.id === "3" && <Onboarding />}
                        {item.id === "4" && <WorkDone />}
                        {item.id === "5" && <Tasks />}
                    </div>
                    {isLoading && <LoadingScreen />}
                </TabStripTab>
            ))}
        </TabStrip>
    ) : (
        <></>
    );
});

export default MatterTabs;
